import { useRef, useState } from 'react'

import { Box, Icon, Text, useToggle } from '@factorialco/deprecated-design-system'
import { F0Button, F0Select } from '@factorialco/f0-react'
import { OneDateNavigator } from '@factorialco/f0-react/dist/experimental'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import moment from 'moment'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useEffect } from 'modules/core/hooks/useEffect'
import { stringToDate } from 'modules/core/lib/dates'
import { firstDayOfWeekAsIndex } from 'modules/core/lib/firstDayOfWeek'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import Selector from 'modules/shift_management/components/ShiftManagement/Content/TableHeader/CommandsFilterBar/ViewSelector/Selector'
import { viewTypeOptions } from 'modules/shift_management/components/ShiftManagement/Content/TableHeader/CommandsFilterBar/ViewSelector/viewTypeOptions'
import ViewOptionsSectionButtonOld from 'modules/shift_management/components/ShiftManagement/Content/TableHeader/ShiftActionsBar/ViewOptionsSectionButton/indexOld'
import useChangeView from 'modules/shift_management/hooks/useChangeView'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { maxDate, minDate } from 'modules/shift_management/lib/getMinMaxDates'
import View from 'modules/shift_management/store/internal/models/View'
import {
  GranularityViewType,
  isValidGranularityViewType,
  transformGranularityViewTypeToViewType,
} from 'modules/shift_management/store/internal/models/View'

interface Props {
  hasNoneShiftManagementPlanning: boolean
}

type DateGranularity = {
  granularity: GranularityViewType
  value: { from: Date; to: Date }
}

const DEFAULT_GRANULARITY: GranularityViewType = 'week'

const getGranularityFromViewType = (viewType: string): GranularityViewType => {
  if (viewType === 'daily') return 'day'
  if (viewType === 'weekly' || viewType === 'four-weeks') return 'week'
  if (viewType === 'monthly') return 'month'
  return DEFAULT_GRANULARITY
}

const NewThemeHeaderViewNavigator = ({ hasNoneShiftManagementPlanning }: Props) => {
  const { view, onChangePeriod, onChangeRoute } = useChangeView()
  const ALL_GRANULARITY_VIEWS: GranularityViewType[] = ['day', 'week', 'month']
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const newEntryFlowEnabled = hasFeatureFlag('dev_shift_management_autoscheduler_entry_flow')
  const hasMonthlyView = hasFeatureFlag('shift_management_monthly_view')
  const viewTypeReadonly = !hasMonthlyView || hasNoneShiftManagementPlanning

  // Load granularity from localStorage with error handling
  const [storedGranularity, setStoredGranularity] = useLocalStorage<GranularityViewType>(
    LOCAL_STORAGE_KEYS.shiftManagementGranularity,
    getGranularityFromViewType(view.viewType)
  )

  // Validate stored granularity and fallback to default if invalid
  const validStoredGranularity =
    storedGranularity && isValidGranularityViewType(storedGranularity) ? storedGranularity : null

  // Track if we've initialized from localStorage to prevent loops
  const hasInitializedFromStorage = useRef(false)
  const isInternalUpdate = useRef(false)
  // Store stable function references to avoid TypeScript errors in useEffect dependencies
  const onChangeRouteRef = useRef(onChangeRoute)
  const setStoredGranularityRef = useRef(setStoredGranularity)

  // Keep refs in sync with latest function references
  onChangeRouteRef.current = onChangeRoute
  setStoredGranularityRef.current = setStoredGranularity

  const viewGranularity = getGranularityFromViewType(view.viewType)
  const initialGranularity: GranularityViewType = validStoredGranularity || viewGranularity

  const [currentDate, setCurrentDate] = useState<DateGranularity>({
    granularity: initialGranularity,
    value: { from: new Date(view.startAt), to: new Date(view.endAt) },
  })

  const { access: currentUser } = useCurrentSession()
  const [{ shiftsRows }] = useShiftsRows()
  const canManageShifts = shiftsRows.some((shiftsRow) => shiftsRow.canManage)

  const [open, toggle] = useToggle(false)

  const { isTimeFormat24H } = useIsTimeFormat24H()
  const borderStyle = { width: 's1', style: 'solid', color: 'grey400' } as const

  const onClickBack = () => onChangePeriod(view.previousPeriod)
  const onClickForward = () => onChangePeriod(view.nextPeriod)

  const onClickToday = () => {
    const today = new View({
      date: moment(),
      viewType: view.viewType,
      groupType: view.groupType,
      isTimeFormat24H,
    })
    onChangePeriod(today)
  }

  const onClickSelect = (date: moment.Moment) => {
    const selectedView = new View({
      date,
      viewType: view.viewType,
      groupType: view.groupType,
      isTimeFormat24H,
    })
    onChangePeriod(selectedView)
    toggle(false)()
  }

  const onClickSelectNew = ({ granularity, value }: DateGranularity) => {
    // needed return as this function is called on every click of the date picker, but we only want it when the date changes
    if (
      value.from.getTime() === currentDate.value.from.getTime() &&
      granularity === currentDate.granularity
    )
      return
    const selectedView = new View({
      date: moment(value.from),
      viewType: view.viewType,
      groupType: view.groupType,
      isTimeFormat24H,
    })
    onChangePeriod(selectedView)

    setCurrentDate({
      granularity: granularity,
      value: { from: value.from, to: value.to },
    })
    toggle(false)()
  }

  const onViewChange = (value: string) => {
    onChangeRoute(`${value}/${view.groupType}`)
  }

  // On initial mount, if stored granularity differs from URL, navigate once
  useEffect(() => {
    if (!hasInitializedFromStorage.current && newEntryFlowEnabled) {
      hasInitializedFromStorage.current = true
      const currentViewGranularity = getGranularityFromViewType(view.viewType)
      if (validStoredGranularity && validStoredGranularity !== currentViewGranularity) {
        isInternalUpdate.current = true
        onChangeRouteRef.current(`${validStoredGranularity}/${view.groupType}`)
      }
    }
  }, [newEntryFlowEnabled, validStoredGranularity, view.viewType, view.groupType])

  // Update granularity when view changes externally (e.g., from URL params or navigation)
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false
      return
    }
    const newViewGranularity = getGranularityFromViewType(view.viewType)
    if (newViewGranularity !== currentDate.granularity) {
      setCurrentDate((prev) => ({
        ...prev,
        granularity: newViewGranularity,
      }))
    }
  }, [view.viewType, currentDate.granularity, newEntryFlowEnabled])

  useUnsafeEffect(() => {
    // needed return as we update the view from outside this component, but we don't re render the component so we only want to update its value when updated outside it
    if (new Date(view.startAt).getDate() === currentDate.value.from.getDate()) return

    setCurrentDate((prev) => ({
      ...prev,
      value: { from: new Date(view.startAt), to: new Date(view.endAt) },
    }))
  }, [view.startAt, view.endAt])

  const onGranularityChange = (granularity: GranularityViewType) => {
    // SafeLocalStorage handles errors internally, so this won't throw
    setStoredGranularity(granularity)

    setCurrentDate((prev) => ({
      ...prev,
      granularity: granularity,
    }))

    isInternalUpdate.current = true
    onChangeRoute(`${granularity}/${view.groupType}`)
  }

  const granularityOptions = ALL_GRANULARITY_VIEWS.map((granularity) => {
    const labelKey =
      granularity === 'day'
        ? i18n.t('shift_management.filters.view_selector.views.daily')
        : granularity === 'week'
        ? i18n.t('shift_management.filters.view_selector.views.weekly')
        : i18n.t('shift_management.filters.view_selector.views.monthly')

    return {
      value: granularity,
      label: labelKey,
    }
  })

  useUnsafeEffect(() => {
    // Only update view when newEntryFlowEnabled is false (old flow)
    // Convert granularity to viewType for the old flow
    // This ensures that when granularity changes (day/week/month), we update the viewType (daily/weekly/monthly)
    // and save it correctly to localStorage as "daily/employees" or "monthly/employees" instead of "week/employees"

    // Skip if this is an internal update to prevent infinite loop
    if (isInternalUpdate.current) {
      return
    }

    if (!newEntryFlowEnabled) {
      const viewType = transformGranularityViewTypeToViewType(currentDate.granularity)
      const currentViewGranularity = getGranularityFromViewType(view.viewType)

      // Only update if the viewType doesn't match the current granularity to prevent infinite loop
      // This ensures we only call onChangeRoute when there's an actual mismatch
      if (currentDate.granularity !== currentViewGranularity) {
        isInternalUpdate.current = true
        onChangeRouteRef.current(`${viewType}/${view.groupType}`)
      }
    }
  }, [currentDate.granularity, newEntryFlowEnabled, view.groupType, view.viewType])

  return (
    <Box flexDirection="row" alignItems="center" gap="s8" minWidth="none" justifyContent="center">
      {!newEntryFlowEnabled && (
        <F0Button
          onClick={onClickToday}
          label={i18n.t('common.today')}
          variant="outline"
          disabled={hasNoneShiftManagementPlanning}
        />
      )}

      {newEntryFlowEnabled && (
        <Box flexDirection="row" alignItems="center" gap="s8">
          <F0Select
            value={currentDate.granularity}
            options={granularityOptions}
            onChange={onGranularityChange}
            label=""
          />
          <OneDateNavigator
            value={{
              granularity: currentDate.granularity,
              value: {
                from: new Date(view.startAt),
                to: new Date(view.endAt),
              },
            }}
            granularities={[currentDate.granularity]}
            onSelect={(selected) => {
              const dateRange = selected?.value
              const granularity = selected?.granularity
              if (dateRange && granularity && isValidGranularityViewType(granularity)) {
                onClickSelectNew({
                  granularity,
                  value: { from: dateRange.from, to: dateRange.to },
                })
              }
            }}
          />
        </Box>
      )}
      {!newEntryFlowEnabled && (
        <PopoverMenu type="dialog" onClick={toggle(!open)} open={open}>
          <Box flexDirection="row" minWidth="s200" height="s32">
            <Box
              width="s32"
              height="full"
              alignItems="center"
              justifyContent="center"
              border={{ all: borderStyle }}
              borderRadius={{ topLeft: 'abs008', bottomLeft: 'abs008' }}
              onClick={onClickBack}
              background="grey000White"
            >
              <Icon.Small icon="chevron-left" color="grey600" />
            </Box>

            <PopoverMenu.Trigger disabled={hasNoneShiftManagementPlanning}>
              <Box flexDirection="row" minWidth="s140" height="s32" background="grey000White">
                <Box
                  flexDirection="row"
                  flexGrow
                  paddingX="s8"
                  alignItems="center"
                  justifyContent="center"
                  gap="s4"
                  border={{ top: borderStyle, bottom: borderStyle }}
                >
                  <Text.Normal
                    weight="normal"
                    color={hasNoneShiftManagementPlanning ? 'grey600' : 'grey800'}
                    ellipsis
                  >
                    {view.headerTitle}
                  </Text.Normal>
                  <Icon.Small icon="chevron-down" color="grey600" />
                </Box>
              </Box>
            </PopoverMenu.Trigger>

            <Box
              width="s32"
              height="full"
              alignItems="center"
              justifyContent="center"
              border={{ all: borderStyle }}
              borderRadius={{ topRight: 'abs008', bottomRight: 'abs008' }}
              onClick={onClickForward}
              background="grey000White"
            >
              <Icon.Small icon="chevron-right" color="grey600" />
            </Box>
          </Box>

          <PopoverMenu.Content side="bottom" align="center" width="s360">
            <DayPicker
              label={i18n.t('common.select')}
              firstDayOfWeek={firstDayOfWeekAsIndex(currentUser.firstDayOfWeek)}
              onChange={(date: string) => onClickSelect(stringToDate(date, 'ui'))}
              minDate={minDate}
              maxDate={maxDate}
              showOnlyCalendar
            />
          </PopoverMenu.Content>
        </PopoverMenu>
      )}
      {!newEntryFlowEnabled && (
        <Selector
          value={view.viewType}
          options={viewTypeOptions(view.groupType)}
          onChange={onViewChange}
          readonly={viewTypeReadonly}
        />
      )}
      {!newEntryFlowEnabled && (
        <ViewOptionsSectionButtonOld
          disabled={hasNoneShiftManagementPlanning}
          canManageShifts={canManageShifts}
        />
      )}
    </Box>
  )
}

export default observer(NewThemeHeaderViewNavigator)
